// Custom hook for workout sounds using Web Audio API
import { useCallback, useRef } from 'react';

const useWorkoutSounds = () => {
  const audioContextRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play a beep sound with specified frequency and duration
  const playBeep = useCallback((frequency = 800, duration = 0.15, volume = 0.3) => {
    try {
      const audioContext = getAudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }, [getAudioContext]);

  // Countdown beeps: 3, 2, 1
  const playCountdown = useCallback((onComplete) => {
    // Beep at 3
    playBeep(600, 0.15, 0.3);
    
    setTimeout(() => {
      // Beep at 2
      playBeep(600, 0.15, 0.3);
    }, 1000);
    
    setTimeout(() => {
      // Beep at 1
      playBeep(600, 0.15, 0.3);
    }, 2000);
    
    setTimeout(() => {
      // Final GO beep (higher pitch, longer)
      playBeep(900, 0.3, 0.4);
      if (onComplete) onComplete();
    }, 3000);
  }, [playBeep]);

  // Sound when exercise starts
  const playExerciseStart = useCallback(() => {
    playBeep(880, 0.2, 0.35);
    setTimeout(() => playBeep(1100, 0.3, 0.4), 150);
  }, [playBeep]);

  // Sound when exercise is completed
  const playExerciseComplete = useCallback(() => {
    playBeep(523, 0.15, 0.3); // C5
    setTimeout(() => playBeep(659, 0.15, 0.3), 100); // E5
    setTimeout(() => playBeep(784, 0.25, 0.35), 200); // G5
  }, [playBeep]);

  // Sound during rest countdown (last 3 seconds)
  const playRestWarning = useCallback(() => {
    playBeep(500, 0.1, 0.25);
  }, [playBeep]);

  // Sound when rest is over
  const playRestComplete = useCallback(() => {
    playBeep(700, 0.15, 0.3);
    setTimeout(() => playBeep(900, 0.2, 0.35), 120);
  }, [playBeep]);

  // Celebration sound when workout is complete
  const playWorkoutComplete = useCallback(() => {
    // Victory fanfare
    const notes = [
      { freq: 523, delay: 0 },    // C5
      { freq: 659, delay: 100 },  // E5
      { freq: 784, delay: 200 },  // G5
      { freq: 1047, delay: 350 }, // C6
      { freq: 784, delay: 500 },  // G5
      { freq: 1047, delay: 600 }, // C6
    ];
    
    notes.forEach(({ freq, delay }) => {
      setTimeout(() => playBeep(freq, 0.2, 0.35), delay);
    });
  }, [playBeep]);

  // Tick sound for timer
  const playTick = useCallback(() => {
    playBeep(400, 0.05, 0.1);
  }, [playBeep]);

  return {
    playBeep,
    playCountdown,
    playExerciseStart,
    playExerciseComplete,
    playRestWarning,
    playRestComplete,
    playWorkoutComplete,
    playTick
  };
};

export default useWorkoutSounds;
